import { useEffect, useRef } from 'react';

type UseRegionLocationSyncParams = {
  availableLocationsIds: string[];
  assignedLocationIds: string[];
  isLoading: boolean;
  hasData: boolean;
  onChange: (locationIds: string[]) => void;
};

export const useRegionLocationSync = ({
  availableLocationsIds,
  assignedLocationIds,
  isLoading,
  hasData,
  onChange,
}: UseRegionLocationSyncParams) => {
  const prevAvailableIdsRef = useRef<string[]>([]);
  const isFirstLoadRef = useRef(true);

  // Sync if available locations changed
  useEffect(() => {
    if (isLoading || !hasData) {
      return;
    }

    // First load
    if (isFirstLoadRef.current) {
      prevAvailableIdsRef.current = availableLocationsIds;
      isFirstLoadRef.current = false;
      return;
    }

    // Sync changes
    const prevAvailableIds = prevAvailableIdsRef.current;
    const prevAvailableIdsSet = new Set(prevAvailableIds);
    const availableLocationIdsSet = new Set(availableLocationsIds);

    const addedIds = availableLocationsIds.filter((id) => {
      return !prevAvailableIdsSet.has(id);
    });

    const removedIds = prevAvailableIds.filter((id) => {
      return !availableLocationIdsSet.has(id);
    });
    const removedIdsSet = new Set(removedIds);

    const remainingIds = assignedLocationIds.filter((id) => {
      return !removedIdsSet.has(id);
    });

    // Set auto-select added location (new region added)
    onChange([...remainingIds, ...addedIds]);
    prevAvailableIdsRef.current = availableLocationsIds;
  }, [availableLocationsIds, isLoading, hasData]);
};
